import { randomId } from "../../lib/random-id";
import { supabase, sessionUser } from "../../lib/supabase";
import {
  type Application,
  type Captain,
  type Team,
  freshApplication,
  recoverGeneratedDraftName,
  text,
} from "./model";
async function rpc<T>(
  name: string,
  args?: Record<string, unknown>,
): Promise<T> {
  const { data, error } = await supabase.rpc(name, args);
  if (error) throw error;
  return data as T;
}
async function currentUser() {
  const user = await sessionUser();
  if (!user) throw Error("Please sign in to continue.");
  return user;
}
async function activeSeason() {
  const { data, error } = await supabase
    .from("seasons")
    .select("id")
    .eq("is_active", true)
    .order("start_date", { ascending: false })
    .limit(1)
    .maybeSingle();
  if (error) throw error;
  return data;
}
export const currentApplication = () =>
  rpc<Application | null>("get_my_season_application");
export const listCaptains = () => rpc<Captain[]>("list_season_captains");
export const listTeams = () => rpc<Team[]>("get_my_season_teams");
export const commitmentOptions = () =>
  rpc<{
    current_tier: string;
    options: { tier: string; eligible: boolean; reason?: string }[];
  }>("get_season_commitment_options");
export async function loadOnboarding() {
  // The user comes from this device's session, so every request below goes out in one round.
  const user = await currentUser();
  const [current, captains, season, { data: profile, error }, isAdmin] = await Promise.all([
    currentApplication(),
    listCaptains(),
    activeSeason(),
    supabase.from("profiles").select("display_name,avatar_url,phone").eq("id", user.id).single(),
    rpc<boolean>("is_admin", { uid: user.id }),
  ]);
  if (error) throw error;
  if (!season) throw Error("There is no active season available.");
  const draftKey = `ll:application:${user.id}:${season.id}`;
  let application = current ?? freshApplication(profile, user.phone);
  if (application.status === "draft") {
    try {
      const saved = JSON.parse(
        localStorage.getItem(draftKey) ?? "null",
      ) as Application | null;
      if (
        saved &&
        saved.status === "draft" &&
        saved.id === application.id &&
        saved.updated_at === application.updated_at
      )
        application = saved;
    } catch {
      /* Server application remains authoritative when draft is invalid. */
    }
  }
  application = recoverGeneratedDraftName(application);
  return { application, captains, draftKey, isAdmin };
}
export async function saveApplication(
  a: Application,
  step: number,
  submit: boolean,
  updateProfile = false,
) {
  if (updateProfile) {
    const user = await currentUser();
    const { error } = await supabase
      .from("profiles")
      .upsert({
        id: user.id,
        display_name: text(a, "name"),
        avatar_url: text(a, "avatar_url") || null,
      });
    if (error) throw error;
  }
  return rpc<Application>("save_season_application", {
    p_data: { ...a.data, step },
    p_role: a.role,
    p_team_id: a.team_id ?? null,
    p_submit: submit,
  });
}
export async function requestTeam(
  role: string,
  teamId: string,
  name: string,
  description: string,
) {
  return rpc("request_season_team", {
    p_role: role,
    p_team_id: role === "member" ? teamId : null,
    p_team_name: role === "captain" ? name : null,
    p_description: role === "captain" ? description : null,
  });
}
export async function listApplications() {
  const [user, season] = await Promise.all([currentUser(), activeSeason()]);
  const { data: rows, error } = season
    ? await supabase
        .from("season_applications")
        .select("*")
        .eq("season_id", season.id)
        .eq("status", "pending")
        .neq("user_id", user.id)
    : { data: [], error: null };
  if (error) throw error;
  const more = await rpc<Application[]>("list_season_team_requests");
  return [
    ...((rows ?? []) as Application[]),
    ...more.map((a) => ({ ...a, additional_team_request: true })),
  ];
}
export const managedMembers = () =>
  rpc<
    {
      user_id: string;
      name: string;
      phone: string;
      avatar_url?: string;
      teams: { name: string }[];
      shipping?: Record<string, string>;
    }[]
  >("list_managed_season_members");
export async function decideApplication(
  a: Application,
  approve: boolean,
  reason?: string,
) {
  return rpc(
    a.additional_team_request
      ? "decide_season_team_request"
      : "decide_season_application",
    {
      [a.additional_team_request ? "p_request_id" : "p_application_id"]: a.id,
      p_approve: approve,
      p_reason: approve ? null : reason?.trim(),
    },
  );
}
export function watchApplicationChanges(tables: string[], refresh: () => void) {
  let active = true;
  const refreshWhileActive = () => {
    if (active) refresh();
  };
  let channel = supabase.channel(`web-season-${randomId()}`);
  for (const table of tables)
    channel = channel.on(
      "postgres_changes",
      { event: "*", schema: "public", table },
      refreshWhileActive,
    );
  channel.subscribe((status) => {
    // Changes can occur before the initial subscription or while disconnected.
    // Reconcile from the server whenever live delivery becomes available again.
    if (status === "SUBSCRIBED") refreshWhileActive();
  });
  return () => {
    active = false;
    void supabase.removeChannel(channel);
  };
}

export async function signOutAccount() {
  const { capabilities, nativeSignOut, isNative } = await import("../../lib/platform");
  if (!isNative()) {
    const {currentBrowserPushToken, clearBrowserPushToken} = await import("../../lib/browser-push");
    const token = currentBrowserPushToken();
    if (token) {
      const user = await currentUser();
      const {error} = await supabase.from("user_push_tokens").update({is_active:false}).eq("user_id",user.id).eq("token",token);
      if (error) throw error;
      await clearBrowserPushToken();
    }
  }
  if (isNative() && capabilities().push) {
    const { FirebaseMessaging } = await import("@capacitor-firebase/messaging");
    const permission = await FirebaseMessaging.checkPermissions();
    if (permission.receive === "granted") {
      const { token } = await FirebaseMessaging.getToken();
      const user = await currentUser();
      const { error } = await supabase
        .from("user_push_tokens")
        .update({ is_active: false })
        .eq("user_id", user.id)
        .eq("token", token);
      if (error) throw error;
      await FirebaseMessaging.deleteToken();
    }
  }
  const user = await currentUser();
  const { cancelAccountUploads } = await import("../../lib/native-upload");
  await cancelAccountUploads(user.id);
  await nativeSignOut();
  const { error } = await supabase.auth.signOut();
  if (error) throw error;
}
